import { Pencil, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Field } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { formatMoney, formatNumber } from '@/lib/format'
import { describeProblem } from '@/lib/problems'
import { normaliseDecimal } from '@/features/finance/lines'
import {
  decimalPlaces,
  emptyCharge,
  isAmountLike,
  isBillableAmount,
  isChargeComplete,
  isRateCeilingRefusal,
  productOf,
  rateCeilingBreach,
  sameAmount,
  type Invoice,
  type InvoiceLinePayload,
} from '@/features/finance/invoice'
import {
  useDropInvoiceLine,
  useWriteInvoiceLine,
} from '@/features/finance/api/useInvoices'

/* The invoice's charge editor — and the surface AC-7 is actually about.
 *
 * AD-10 refuses a line that would take the client's billed total past the
 * approved rate AT THE POINT OF ADDING IT, not at issue. So every charge is its
 * own round trip: the row appears when the server has accepted it, and a
 * refusal lands beside the amount that caused it while the typist is still
 * looking at that amount. Collecting rows locally and posting them on submit
 * would move the refusal to the end of the job, which is the discovery-too-late
 * failure this story exists to remove.
 *
 * That also settles UX-DR-24 here without a second rule: money operations wait
 * for the server, so nothing below flips a cache ahead of a response.
 *
 * The refusal message is composed HERE, from `approvedRateUsd` and `billedUsd`
 * on the invoice already in hand. The server sends no figures inside the error
 * on purpose (finance/handlers.go) — a sentence built there would be English on
 * a Russian screen (AD-27).
 */

export function InvoiceLineEditor({
  caseId,
  invoice,
}: {
  caseId: string
  invoice: Invoice
}) {
  const { t } = useTranslation()
  const write = useWriteInvoiceLine(caseId, invoice.id)
  const drop = useDropInvoiceLine(caseId, invoice.id)

  const [draft, setDraft] = useState<InvoiceLinePayload>(emptyCharge)
  /** The line being edited, or undefined while adding. */
  const [editing, setEditing] = useState<string | undefined>(undefined)
  const [touched, setTouched] = useState(false)

  /* Any keystroke clears the last refusal. The message names a figure, and
     leaving it under a field that has since been retyped would have it naming
     a figure nobody can see any more.

     ── The amount FOLLOWS the rate and the count ─────────────────────────────

     Touching either side of the multiplication rewrites the amount, and this
     used to be the one thing this editor would not do: the product was
     computed, shown beside the box as a check figure, and then left for the
     typist to copy into the box by hand. The comment defending that said the
     server bills `amount` and a figure the browser multiplied would be a
     second answer — but the second answer was being produced either way, next
     to the field, and the only difference was whose fingers retyped it. What
     it actually bought was a class of mistake that no validation can catch: a
     rate corrected from 1 850 to 1 900 while the amount below it still says
     3 700, which is a demand the client pays without noticing and nobody can
     reconstruct afterwards.

     It is a DEFAULT, not a lock. The amount stays a plain field, so a charge
     that is genuinely not the product of its own columns — a rounded lump, a
     figure agreed on the phone — is still typed straight in and survives,
     right up until somebody edits a column beside it and thereby says the
     multiplication is what they meant.

     An empty count multiplies as ONE rather than as nothing, which is what
     makes an instalment line behave. A tranche prefill arrives as a lump sum
     with no quantity and no unit price (see the server's `trancheLines`), so
     typing a price into it with the count left blank means "this, once" — and
     answering that with a blank amount would leave the old instalment figure
     standing under a price that contradicts it. The blank is not filled in on
     screen: it is a lump sum, and printing `1` on the client's paper would be
     the browser inventing a breakdown nobody stated. */
  const patch = (change: Partial<InvoiceLinePayload>) => {
    if (write.isError) write.reset()
    setDraft((current) => {
      const next = { ...current, ...change }
      if (!('quantity' in change) && !('unitPrice' in change)) return next

      const product = productOf(next.quantity.trim() === '' ? '1' : next.quantity, next.unitPrice)
      return product === undefined ? next : { ...next, amount: product }
    })
  }

  const startEdit = (lineId: string) => {
    const line = invoice.lines.find((candidate) => candidate.id === lineId)
    if (!line) return
    if (write.isError) write.reset()
    setTouched(false)
    setEditing(lineId)
    setDraft({
      description: line.description,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      amount: line.amount,
    })
  }

  const cancelEdit = () => {
    if (write.isError) write.reset()
    setTouched(false)
    setEditing(undefined)
    setDraft(emptyCharge())
  }

  /** Removes a charge, and closes the form if it was the one open in it.
   *
   *  Without the second half the editor goes on pointing at a line that no
   *  longer exists: the fields still hold its figures, and submitting PUTs
   *  /lines/<deleted id>, which answers a not-found the user cannot act on
   *  because nothing on the screen says which line it means. */
  const removeLine = (lineId: string) => {
    if (editing === lineId) cancelEdit()
    drop.mutate(lineId)
  }

  const complete = isChargeComplete(draft)

  const submit = () => {
    setTouched(true)
    if (!complete || write.isPending) return
    write.mutate(
      { ...draft, lineId: editing },
      {
        onSuccess: () => {
          setTouched(false)
          setEditing(undefined)
          setDraft(emptyCharge())
        },
      },
    )
  }

  /* The AD-10 sentence, or undefined when the refusal was something else.
     `write.variables` is the charge the server actually judged — reading the
     live draft instead would name whatever has been typed since. */
  const refused = write.isError && isRateCeilingRefusal(write.error)
  const breach = refused
    ? rateCeilingBreach(
        invoice,
        write.variables?.amount ?? '0',
        // An edit replaces a figure that is already inside `billedUsd`, so it
        // is taken back out before the excess is named. Without this an edit
        // from 100 to 120 would be reported as 120 over rather than 20.
        invoice.lines.find((line) => line.id === write.variables?.lineId)?.amount ?? '0',
      )
    : undefined

  /* Three answers for one box, most specific first. A figure with a third
     decimal place IS a number, so `invalidNumber` would send the typist looking
     for a fault that is not there — the amount is billed into a two-place
     column and the message has to say so. */
  const amountError = refused
    ? t('finance.invoices.lines.exceedsShort')
    : !touched
      ? undefined
      : !isAmountLike(draft.amount)
        ? t('common.form.invalidNumber')
        : !isBillableAmount(draft.amount)
          ? t('common.form.tooManyDecimals')
          : Number(normaliseDecimal(draft.amount)) <= 0
            ? // A number, billable, and zero. Refused by the server and by
              // `isChargeComplete`, and said in its own words: "Enter a number"
              // over a 0 somebody typed on purpose explains nothing.
              t('finance.invoices.lines.amountPositive')
            : undefined

  /* The two optional boxes, which had no message at all.
     `isChargeComplete` refuses "abc", "-1" and a seventh decimal place in
     either of them, so submit() returned without writing anything and without
     saying why — a button that visibly does nothing when it is pressed. Each
     box now answers for itself, beside itself. */
  const quantityError =
    touched && draft.quantity !== '' && !isAmountLike(draft.quantity)
      ? t('common.form.invalidNumber')
      : undefined
  const unitPriceError =
    touched && draft.unitPrice !== '' && !isAmountLike(draft.unitPrice)
      ? t('common.form.invalidNumber')
      : undefined

  /* The row's own arithmetic, and it is on screen only when it DISAGREES with
     the figure being billed.

     It used to be shown whenever both columns parsed, which was the right
     shape while the amount was typed by hand: the product was the check figure
     to compare against. Now that `patch` writes the product into the amount,
     showing it in the ordinary case would repeat the number in the box
     directly under it — and a hint that restates its own field trains people
     to stop reading hints.

     So it survives for the one case it still answers: an amount that is NOT
     the product of the columns beside it, because somebody typed a lump sum
     over it. That is legitimate and stays legitimate — see `patch` — but it is
     worth saying out loud, since the same appearance is also what a half-done
     edit looks like.

     Computed exactly rather than in floats: a check figure that is a cent out
     sends the typist to correct a figure that was right (see productOf). The
     empty count multiplies as one, matching what `patch` does, or a lump-sum
     line with a price typed into it would report itself as disagreeing with a
     product it never had. */
  const product =
    isAmountLike(draft.unitPrice) &&
    (draft.quantity.trim() === '' || isAmountLike(draft.quantity))
      ? productOf(draft.quantity.trim() === '' ? '1' : draft.quantity, draft.unitPrice)
      : undefined
  const preview =
    product !== undefined && !sameAmount(product, draft.amount) ? product : undefined

  return (
    <section className="flex flex-col gap-2.5" aria-label={t('finance.invoices.lines.label')}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-text-body text-[12.5px] leading-none font-normal">
          {t('finance.invoices.lines.label')}
        </span>
        <span className="flex items-baseline gap-2">
          <span className="asa-eyebrow">{t('common.fields.total')}</span>
          <span className="asa-num text-text-heading text-[15px] font-semibold">
            {formatMoney(invoice.totalAmount, invoice.currency)}
          </span>
        </span>
      </div>

      {invoice.lines.length === 0 ? (
        <p className="text-text-muted text-[12px] leading-snug font-light">
          {t('finance.invoices.lines.empty')}
        </p>
      ) : (
        <ul className="flex flex-col gap-1.5">
          {invoice.lines.map((line, index) => (
            <li
              key={line.id}
              className={cn(
                'bg-surface-sunken border-line-hair flex items-start gap-2 rounded-[var(--radius-md)] border p-2.5',
                editing === line.id && 'border-brand',
              )}
            >
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="text-text-heading text-[13px]">{line.description}</span>
                {/* Only when there is a PRICE to multiply by. A quantity on its
                    own renders "3.00 × —", which reads as a figure the server
                    lost rather than as a lump-sum charge nobody broke down —
                    and the amount beside it is the whole truth anyway. */}
                {/* Each figure at the precision it was STORED with, not at
                    two places. A quantity is not money — it validates to six
                    places and the column keeps three — so 1.375 rendered as
                    "1.38 × 100.00" is a breakdown that no longer multiplies to
                    the amount printed beside it, which is the one thing this
                    line exists to let a reader check. */}
                {line.unitPrice !== '' ? (
                  <span className="asa-num text-text-muted text-[12px] font-light">
                    {quantityFigure(line.quantity)}
                    {' × '}
                    {formatNumber(line.unitPrice, decimalPlaces(line.unitPrice))}
                  </span>
                ) : null}
              </span>

              <span className="asa-num text-text-heading shrink-0 pt-0.5 text-[13px] font-semibold">
                {formatMoney(line.amount, line.currency)}
              </span>

              <button
                type="button"
                onClick={() => startEdit(line.id)}
                aria-label={t('finance.invoices.lines.editN', { n: index + 1 })}
                title={t('finance.invoices.lines.editN', { n: index + 1 })}
                className="text-text-faint hover:text-brand hover:bg-surface-hover flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-md)] transition-colors"
              >
                <Pencil size={14} aria-hidden />
              </button>

              {/* Icon-only, like the debit note's row remove and for the same
                  reason: a labelled button per row would crowd out the figures.
                  The label lives in aria-label and the tooltip. */}
              <button
                type="button"
                onClick={() => removeLine(line.id)}
                disabled={drop.isPending}
                aria-label={t('finance.invoices.lines.remove', { n: index + 1 })}
                title={t('finance.invoices.lines.remove', { n: index + 1 })}
                className="text-text-faint hover:text-negative hover:bg-negative-soft flex size-8 shrink-0 cursor-pointer items-center justify-center rounded-[var(--radius-md)] transition-colors"
              >
                <Trash2 size={14} aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {drop.isError ? (
        <p role="alert" className="text-negative text-[12.5px] leading-snug">
          {t(describeProblem(drop.error).detailKey)}
        </p>
      ) : null}

      {/* The refusal sits ABOVE the charge it concerns, which is where this
          system puts a record-level message; the field beside the amount says
          which input to fix. Both, because one names the rule and the other
          names the box. */}
      {breach ? (
        <p
          role="alert"
          className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
        >
          {t('finance.invoices.lines.exceedsApprovedRate', {
            approved: formatMoney(breach.approvedUsd, 'USD'),
            billed: formatMoney(breach.billedUsd, 'USD'),
            excess: formatMoney(breach.excessUsd, 'USD'),
          })}
        </p>
      ) : refused ? (
        // Refused on the ceiling, but the case carries no approved rate this
        // client can quote back. Say what happened rather than inventing a
        // figure to explain it with.
        <p
          role="alert"
          className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
        >
          {t('finance.invoices.lines.exceedsUnknownRate')}
        </p>
      ) : write.isError ? (
        <p
          role="alert"
          className="bg-negative-soft text-negative rounded-md px-3 py-2 text-[12.5px] leading-snug"
        >
          {t(describeProblem(write.error).detailKey)}
        </p>
      ) : null}

      <form
        className="border-line-hair flex flex-col gap-2.5 rounded-[var(--radius-md)] border border-dashed p-2.5"
        onSubmit={(event) => {
          event.preventDefault()
          event.stopPropagation()
          submit()
        }}
        noValidate
      >
        {/* The record-level backstop. Every branch of `isChargeComplete` has a
            message beside its own box above, and this is what keeps that true
            when a later condition is added without one: pressing the button
            always says something. */}
        {touched && !complete ? (
          <p role="alert" className="text-negative text-[12.5px] leading-snug">
            {t('common.form.fixFields')}
          </p>
        ) : null}

        <Field
          label={t('finance.invoices.lines.description')}
          required
          error={
            touched && draft.description.trim() === ''
              ? t('common.form.required')
              : undefined
          }
        >
          {(props) => (
            <Input
              value={draft.description}
              onChange={(e) => patch({ description: e.target.value })}
              {...props}
            />
          )}
        </Field>

        <div className="flex gap-2.5">
          <Field
            label={t('finance.invoices.lines.quantity')}
            hint={t('common.form.optional')}
            error={quantityError}
            className="w-24 shrink-0"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.quantity}
                onChange={(e) => patch({ quantity: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          <Field
            label={t('finance.invoices.lines.unitPrice')}
            hint={t('common.form.optional')}
            error={unitPriceError}
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.unitPrice}
                onChange={(e) => patch({ unitPrice: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>

          {/* The billed figure. Required, and NOT derived from the two fields
              beside it: most charges are a lump sum with neither, and a server
              that recomputed this would round a number the client already
              agreed to. */}
          <Field
            label={t('finance.invoices.lines.amount')}
            required
            error={amountError}
            hint={
              preview === undefined
                ? t('finance.invoices.lines.amountHint', {
                    currency: invoice.currency,
                  })
                : t('finance.invoices.lines.amountPreview', {
                    amount: formatNumber(preview, 2),
                  })
            }
            className="flex-1"
          >
            {(props) => (
              <Input
                inputMode="decimal"
                value={draft.amount}
                onChange={(e) => patch({ amount: e.target.value })}
                className="asa-num"
                {...props}
              />
            )}
          </Field>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="submit"
            variant="secondary"
            size="sm"
            iconLeft={editing === undefined ? Plus : undefined}
            disabled={write.isPending}
          >
            {write.isPending
              ? t('common.form.saving')
              : editing === undefined
                ? t('finance.invoices.lines.add')
                : t('finance.invoices.lines.saveCharge')}
          </Button>

          {editing !== undefined ? (
            <Button variant="ghost" size="sm" iconLeft={X} onClick={cancelEdit}>
              {t('common.actions.cancel')}
            </Button>
          ) : null}
        </div>
      </form>
    </section>
  )
}

/** One stored line's quantity, at the precision it was written with.
 *
 *  An absent quantity renders as 1, because the row's unit price IS its amount
 *  and "× 100.00" with nothing in front of it reads as a figure the page lost. */
function quantityFigure(quantity: string): string {
  const figure = quantity === '' ? '1' : quantity
  return formatNumber(figure, decimalPlaces(figure))
}
